import { useCallback, useEffect } from 'react'
import { useSetAtom } from 'jotai'

import { pendingDisplayAtom } from '../staged-action-local-state'

const METADATA_FLUSH_DELAY_MS = 500

const pendingMetadataUpdates = new Map<string, Record<string, string>>()
let metadataFlushTimer: ReturnType<typeof setTimeout> | null = null
let metadataFlushFn: (() => void) | null = null
let metadataFlushRefCount = 0

export function schedulePendingMetadata(id: string, updates: Record<string, string>): void {
  const existing = pendingMetadataUpdates.get(id)
  pendingMetadataUpdates.set(id, existing ? { ...existing, ...updates } : updates)
  if (metadataFlushTimer) clearTimeout(metadataFlushTimer)
  metadataFlushTimer = setTimeout(() => {
    metadataFlushFn?.()
  }, METADATA_FLUSH_DELAY_MS)
}

export function flushPendingMetadataNow(): void {
  if (metadataFlushTimer) {
    clearTimeout(metadataFlushTimer)
    metadataFlushTimer = null
  }
  metadataFlushFn?.()
}

export function useMetadataFlush(
  bulkPatchMetadata: (updates: Array<{ id: string; metadata: Record<string, string> }>) => Promise<void>,
): void {
  useEffect(() => {
    metadataFlushRefCount++
    metadataFlushFn = () => {
      if (pendingMetadataUpdates.size === 0) return
      const batch = Array.from(pendingMetadataUpdates.entries()).map(([id, metadata]) => ({ id, metadata }))
      pendingMetadataUpdates.clear()
      void bulkPatchMetadata(batch)
    }
    return () => {
      metadataFlushRefCount--
      if (metadataFlushRefCount === 0 && pendingMetadataUpdates.size > 0) {
        const batch = Array.from(pendingMetadataUpdates.entries()).map(([id, metadata]) => ({ id, metadata }))
        pendingMetadataUpdates.clear()
        void bulkPatchMetadata(batch)
      }
    }
  }, [bulkPatchMetadata])
}

/** Removes a single id from the in-session pending-display tracker. */
export function useClearPendingDisplay(): (id: string) => void {
  const setPendingDisplay = useSetAtom(pendingDisplayAtom)
  return useCallback(
    (id: string) => {
      setPendingDisplay((prev) => {
        if (!prev.has(id)) return prev
        const next = new Map(prev)
        next.delete(id)
        return next
      })
    },
    [setPendingDisplay],
  )
}
